import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaDollarSign,
  FaFilter,
  FaEye,
  FaBuilding,
  FaProjectDiagram,
  FaChartBar,
  FaTrophy,
  FaAward
} from 'react-icons/fa';

interface JobHistory {
  id: string;
  taskId: string;
  projectId: string;
  businessId: string;
  taskTitle: string;
  projectName: string;
  businessName: string;
  startDate: string;
  endDate: string;
  duration: number;
  durationUnit: 'hours' | 'days';
  finalPrice: number;
  finalPriceUnit: 'hour' | 'day';
  finalTotal: number;
  fee: number;
  netAmount: number;
  status: 'completed' | 'cancelled' | 'in_progress';
  rating?: number;
  review?: string;
  location: {
    address: string;
    city: string;
    state: string;
    country: string;
  };
  completedAt: any;
}

interface Statistics {
  totalJobs: number;
  totalEarnings: number;
  averageRating: number;
  totalReviews: number;
  totalHours: number;
  totalDays: number;
  completionRate: number;
  averageJobValue: number;
  currentMonth: {
    month: string;
    earnings: number;
    jobs: number;
  };
  bestMonth: {
    month: string;
    earnings: number;
    jobs: number;
  };
}

const WorkerJobHistory: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const db = getFirestore();
  
  const [jobHistory, setJobHistory] = useState<JobHistory[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtros
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    minAmount: '',
    maxAmount: '',
    business: 'all'
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [businesses, setBusinesses] = useState<string[]>([]);

  useEffect(() => {
    fetchJobHistory();
  }, [currentUser]);

  const fetchJobHistory = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Obtener todas las transacciones de trabajo (pendientes y completadas)
      const walletRef = collection(db, 'wallet', currentUser.uid, 'transacciones');
      const walletQuery = query(
        walletRef,
        where('tipo', '==', 'pendiente_pago'),
        orderBy('fecha', 'desc')
      );
      
      const walletSnap = await getDocs(walletQuery);
      const jobs: JobHistory[] = [];
      const businessSet = new Set<string>();
      
      for (const docSnapshot of walletSnap.docs) {
        const data = docSnapshot.data();
        
        try {
          // Obtener información de la tarea
          const taskRef = doc(db, `projects/${data.businessId}/projects/${data.projectId}/tasks/${data.taskId}`);
          const taskSnap = await getDoc(taskRef);
          
          if (taskSnap.exists()) {
            const taskData = taskSnap.data();
            
            // Obtener información del proyecto
            const projectRef = doc(db, `projects/${data.businessId}/projects/${data.projectId}`);
            const projectSnap = await getDoc(projectRef);
            const projectData = projectSnap.exists() ? projectSnap.data() : {};
            
            // Obtener información del business
            const businessRef = doc(db, 'businesses', data.businessId);
            const businessSnap = await getDoc(businessRef);
            const businessData = businessSnap.exists() ? businessSnap.data() : {};
            
            // Determinar el estado basado en la data
            let jobStatus: 'completed' | 'cancelled' | 'in_progress' = 'in_progress';
            if (data.estado === 'completado' || data.estado === 'pagado') {
              jobStatus = 'completed';
            } else if (data.estado === 'cancelado') {
              jobStatus = 'cancelled';
            }
            
            const job: JobHistory = {
              id: docSnapshot.id,
              taskId: data.taskId,
              projectId: data.projectId,
              businessId: data.businessId,
              taskTitle: data.taskTitle || (taskData as any).title || 'Sin título',
              projectName: (projectData as any).name || 'Sin nombre',
              businessName: (businessData as any).companyName || (businessData as any).name || 'Sin nombre',
              startDate: data.startDate || (taskData as any).startDate || '',
              endDate: data.fecha || '',
              duration: data.duration || (taskData as any).duration || 0,
              durationUnit: data.durationUnit || (taskData as any).durationUnit || 'hours',
              finalPrice: data.finalPrice || (taskData as any).finalPrice || 0,
              finalPriceUnit: data.finalPriceUnit || (taskData as any).finalPriceUnit || 'hour',
              finalTotal: data.total || 0,
              fee: data.fee || 0,
              netAmount: data.monto || 0,
              status: jobStatus,
              location: (taskData as any).location || {},
              completedAt: data.fecha || ''
            };
            
            jobs.push(job);
            businessSet.add((businessData as any).companyName || (businessData as any).name || 'Sin nombre');
          }
        } catch (err) {
          console.error('Error fetching job details:', err);
        }
      }
      
      setJobHistory(jobs);
      setBusinesses(Array.from(businessSet));
      calculateStatistics(jobs);
      
    } catch (err) {
      setError(t('jobHistory.error.loading'));
      console.error('Error loading job history:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (jobs: JobHistory[]) => {
    if (jobs.length === 0) {
      setStatistics(null);
      return;
    }

    const totalJobs = jobs.length;
    const totalEarnings = jobs.reduce((sum, job) => sum + job.netAmount, 0);
    const totalHours = jobs.reduce((sum, job) => 
      sum + (job.durationUnit === 'hours' ? job.duration : job.duration * 8), 0
    );
    const totalDays = jobs.reduce((sum, job) => 
      sum + (job.durationUnit === 'days' ? job.duration : job.duration / 8), 0
    );
    
    // Filtrar trabajos del mes actual
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const currentMonthJobs = jobs.filter(job => {
      let jobDate: Date;
      
      // Convertir fecha de Firestore a Date
      if (job.completedAt?.toDate) {
        jobDate = job.completedAt.toDate();
      } else if (job.completedAt?.seconds) {
        jobDate = new Date(job.completedAt.seconds * 1000);
      } else {
        jobDate = new Date(job.completedAt);
      }
      
      return jobDate.getMonth() === currentMonth && jobDate.getFullYear() === currentYear;
    });
    
    const currentMonthEarnings = currentMonthJobs.reduce((sum, job) => sum + job.netAmount, 0);
    const currentMonthName = now.toLocaleDateString(i18n.language, { 
      year: 'numeric', 
      month: 'long' 
    });

    // Calcular estadísticas por mes para encontrar el mejor mes histórico
    const monthlyStats = jobs.reduce((acc, job) => {
      let jobDate: Date;
      
      if (job.completedAt?.toDate) {
        jobDate = job.completedAt.toDate();
      } else if (job.completedAt?.seconds) {
        jobDate = new Date(job.completedAt.seconds * 1000);
      } else {
        jobDate = new Date(job.completedAt);
      }
      
      const monthKey = `${jobDate.getFullYear()}-${jobDate.getMonth()}`;
      const monthName = jobDate.toLocaleDateString(i18n.language, { 
        year: 'numeric', 
        month: 'long' 
      });
      
      if (!acc[monthKey]) {
        acc[monthKey] = { month: monthName, earnings: 0, jobs: 0 };
      }
      
      acc[monthKey].earnings += job.netAmount;
      acc[monthKey].jobs += 1;
      
      return acc;
    }, {} as Record<string, { month: string; earnings: number; jobs: number }>);

    const bestMonth = Object.values(monthlyStats).reduce((best, current) => {
      return current.earnings > best.earnings ? current : best;
    }, { month: '', earnings: 0, jobs: 0 });

    const stats: Statistics = {
      totalJobs,
      totalEarnings,
      averageRating: 0, // TODO: Implementar sistema de ratings
      totalReviews: 0,  // TODO: Implementar sistema de reviews
      totalHours,
      totalDays,
      completionRate: 100, // Todos los trabajos están completados
      averageJobValue: totalEarnings / totalJobs,
      currentMonth: {
        month: currentMonthName,
        earnings: currentMonthEarnings,
        jobs: currentMonthJobs.length
      },
      bestMonth
    };

    setStatistics(stats);
  };

  const filteredJobs = jobHistory.filter(job => {
    if (filters.status !== 'all' && job.status !== filters.status) return false;
    if (filters.business !== 'all' && job.businessName !== filters.business) return false;
    if (filters.minAmount && job.netAmount < parseFloat(filters.minAmount)) return false;
    if (filters.maxAmount && job.netAmount > parseFloat(filters.maxAmount)) return false;
    
    if (filters.dateRange !== 'all') {
      let jobDate: Date;
      
      // Convertir fecha de Firestore a Date
      if (job.completedAt?.toDate) {
        jobDate = job.completedAt.toDate();
      } else if (job.completedAt?.seconds) {
        jobDate = new Date(job.completedAt.seconds * 1000);
      } else {
        jobDate = new Date(job.completedAt);
      }
      
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - jobDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (filters.dateRange) {
        case 'week':
          if (diffDays > 7) return false;
          break;
        case 'month':
          if (diffDays > 30) return false;
          break;
        case 'quarter':
          if (diffDays > 90) return false;
          break;
        case 'year':
          if (diffDays > 365) return false;
          break;
      }
    }
    
    return true;
  });

  const formatDate = (dateString: any) => {
    let date: Date;
    
    // Convertir fecha de Firestore a Date
    if (dateString?.toDate) {
      date = dateString.toDate();
    } else if (dateString?.seconds) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/worker-dashboard')}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
          >
            <FaArrowLeft />
            <span>{t('common.back')}</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-800">{t('jobHistory.title')}</h1>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            <FaFilter />
            <span>{t('common.filters')}</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Filtros */}
        {showFilters && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">{t('jobHistory.filters')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('jobHistory.filterStatus')}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="completed">{t('jobHistory.status.completed')}</option>
                  <option value="cancelled">{t('jobHistory.status.cancelled')}</option>
                  <option value="in_progress">{t('jobHistory.status.inProgress')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('jobHistory.filterDateRange')}
                </label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="week">{t('jobHistory.dateRange.week')}</option>
                  <option value="month">{t('jobHistory.dateRange.month')}</option>
                  <option value="quarter">{t('jobHistory.dateRange.quarter')}</option>
                  <option value="year">{t('jobHistory.dateRange.year')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('jobHistory.filterBusiness')}
                </label>
                <select
                  value={filters.business}
                  onChange={(e) => setFilters(prev => ({ ...prev, business: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="all">{t('common.all')}</option>
                  {businesses.map(business => (
                    <option key={business} value={business}>{business}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('jobHistory.filterMinAmount')}
                </label>
                <input
                  type="number"
                  value={filters.minAmount}
                  onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('jobHistory.filterMaxAmount')}
                </label>
                <input
                  type="number"
                  value={filters.maxAmount}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                  placeholder="∞"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        {/* Estadísticas */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('jobHistory.stats.totalJobs')}</p>
                  <p className="text-2xl font-bold text-blue-600">{statistics.totalJobs}</p>
                </div>
                <FaProjectDiagram className="text-3xl text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('jobHistory.stats.totalEarnings')}</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.totalEarnings)}</p>
                </div>
                <FaDollarSign className="text-3xl text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('jobHistory.stats.averageJobValue')}</p>
                  <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.averageJobValue)}</p>
                </div>
                <FaChartBar className="text-3xl text-purple-500" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('jobHistory.stats.completionRate')}</p>
                  <p className="text-2xl font-bold text-yellow-600">{statistics.completionRate}%</p>
                </div>
                <FaTrophy className="text-3xl text-yellow-500" />
              </div>
            </div>
          </div>
        )}

        {/* Estadísticas mensuales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Mes Actual */}
          {statistics?.currentMonth && (
            <div className="bg-gradient-to-r from-blue-400 to-blue-600 rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">{t('jobHistory.currentMonth.title')}</h3>
                  <p className="text-white text-sm">{statistics.currentMonth.month}</p>
                  <p className="text-white font-semibold">
                    {formatCurrency(statistics.currentMonth.earnings)} - {statistics.currentMonth.jobs} {t('jobHistory.currentMonth.jobs')}
                  </p>
                </div>
                <FaCalendarAlt className="text-4xl text-white" />
              </div>
            </div>
          )}

          {/* Mejor Mes */}
          {statistics?.bestMonth && statistics.bestMonth.jobs > 0 && (
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">{t('jobHistory.bestMonth.title')}</h3>
                  <p className="text-white text-sm">{statistics.bestMonth.month}</p>
                  <p className="text-white font-semibold">
                    {formatCurrency(statistics.bestMonth.earnings)} - {statistics.bestMonth.jobs} {t('jobHistory.bestMonth.jobs')}
                  </p>
                </div>
                <FaAward className="text-4xl text-white" />
              </div>
            </div>
          )}
        </div>

        {/* Lista de trabajos */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              {t('jobHistory.jobsList')} ({filteredJobs.length})
            </h2>
          </div>

          {filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <FaProjectDiagram className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('jobHistory.noJobs')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">{job.taskTitle}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {t(`jobHistory.status.${job.status === 'in_progress' ? 'inProgress' : job.status}`)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaBuilding className="text-red-500" />
                          <span>{job.businessName}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaProjectDiagram className="text-blue-500" />
                          <span>{job.projectName}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaCalendarAlt className="text-green-500" />
                          <span>{formatDate(job.completedAt)}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaMapMarkerAlt className="text-purple-500" />
                          <span>{job.location.city}, {job.location.state}</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600">{t('jobHistory.duration')}</p>
                          <p className="font-semibold text-gray-800">
                            {job.duration} {t(`jobHistory.units.${job.durationUnit}`)}
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600">{t('jobHistory.rate')}</p>
                          <p className="font-semibold text-gray-800">
                            {formatCurrency(job.finalPrice)} {t(`jobHistory.per.${job.finalPriceUnit}`)}
                          </p>
                        </div>
                        
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600">{t('jobHistory.earnings')}</p>
                          <p className="font-semibold text-green-600">{formatCurrency(job.netAmount)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => navigate(`/worker-projects/${job.projectId}/tasks/${job.taskId}`)}
                        className="text-blue-600 hover:text-blue-700"
                        title={t('common.view')}
                      >
                        <FaEye />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerJobHistory; 